// Display formatters used across modules. Localized to en-US for now.

import { formatDistanceToNowStrict, format } from "date-fns";

export const fmtDate = (iso: string | null | undefined, fallback = "—"): string => {
  if (!iso) return fallback;
  try {
    return format(new Date(iso), "MMM d, yyyy");
  } catch {
    return fallback;
  }
};

export const fmtDateTime = (iso: string | null | undefined, fallback = "—"): string => {
  if (!iso) return fallback;
  try {
    return format(new Date(iso), "MMM d, yyyy h:mm a");
  } catch {
    return fallback;
  }
};

export const fmtRelative = (iso: string | null | undefined, fallback = "—"): string => {
  if (!iso) return fallback;
  try {
    return formatDistanceToNowStrict(new Date(iso), { addSuffix: true });
  } catch {
    return fallback;
  }
};

export const fmtRelativeShort = (iso: string | null | undefined): string => {
  if (!iso) return "";
  try {
    return formatDistanceToNowStrict(new Date(iso));
  } catch {
    return "";
  }
};

export const fmtMoney = (
  cents: number | null | undefined,
  currency = "USD",
  opts: { showCents?: boolean } = {},
): string => {
  if (cents == null) return "—";
  const amount = cents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: opts.showCents ? 2 : 0,
    maximumFractionDigits: opts.showCents ? 2 : 0,
  }).format(amount);
};

export const fmtMoneyDollars = (dollars: number | null | undefined): string => {
  if (dollars == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(dollars);
};

export const fmtNumber = (n: number | null | undefined): string => {
  if (n == null) return "—";
  return new Intl.NumberFormat("en-US").format(n);
};

export const fmtPercent = (n: number | null | undefined, decimals = 0): string => {
  if (n == null) return "—";
  return `${n.toFixed(decimals)}%`;
};

/** Map a status / lifecycle / score string to a brand-colored badge variant. */
export const pillVariant = (status: string | null | undefined):
  "default" | "success" | "warn" | "danger" | "accent" | "muted" | "outline" => {
  if (!status) return "muted";
  const s = status.toLowerCase();
  if (["active", "signed", "approved", "completed", "warm", "hot", "delivered", "connected"].includes(s)) return "success";
  if (["draft", "pending", "pending_approval", "scheduled", "verified"].includes(s)) return "warn";
  if (["expired", "declined", "rejected", "stopped", "denied", "missing", "failed", "blazing"].includes(s)) return "danger";
  if (["sent", "open", "ready", "executing"].includes(s)) return "default";
  if (["nurture", "lead", "qualified", "customer", "evangelist"].includes(s)) return "accent";
  return "muted";
};
